import React, { useState, useEffect, useCallback } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'a11y-settings';

const DEFAULT_SETTINGS = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const applySettings = useCallback((s) => {
    const html = document.documentElement;

    // Font size
    html.style.fontSize = s.fontSize + '%';

    // Toggle classes
    html.classList.toggle('a11y-high-contrast', s.highContrast);
    html.classList.toggle('a11y-highlight-links', s.highlightLinks);
    html.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);
    html.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage unavailable
    }
  }, [settings, applySettings]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const adjustFontSize = (delta) => {
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.min(150, Math.max(75, prev.fontSize + delta)),
    }));
  };

  const resetAll = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <>
      <button
        className="a11y-widget__toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Accessibility settings"
        aria-expanded={isOpen}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {isOpen && (
        <div className="a11y-widget__panel" role="dialog" aria-label="Accessibility settings">
          <div className="a11y-widget__panel-header">
            <h2 className="a11y-widget__panel-title">Accessibility</h2>
            <button
              className="a11y-widget__panel-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close accessibility settings"
            >
              &times;
            </button>
          </div>

          {/* Font Size */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(-10)}
                aria-label="Decrease font size"
              >
                A&minus;
              </button>
              <span className="a11y-widget__font-size-display">{settings.fontSize}%</span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFontSize(10)}
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">High Contrast</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highContrast}
                onChange={(e) => updateSetting('highContrast', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Highlight Links</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highlightLinks}
                onChange={(e) => updateSetting('highlightLinks', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.dyslexiaFont}
                onChange={(e) => updateSetting('dyslexiaFont', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__control">
            <span className="a11y-widget__label">Reduce Motion</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => updateSetting('reducedMotion', e.target.checked)}
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reset */}
          <button className="a11y-widget__reset" onClick={resetAll}>
            Reset All
          </button>
        </div>
      )}
    </>
  );
}

export default AccessibilityWidget;
